import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { getUrl, Urls } from 'app/UrlService'
import { refetchRoles } from 'data/hooks/roles'
import { refetchStacks } from 'data/hooks/stacks/stackOperations'
import { useAppTemplates, useInstallAppTemplate } from 'data/templates/appTemplates'
import { ModalPane } from 'features/workspace/createAppModalTypes'

import { useToast } from 'ui/components/Toast'

const LOADING_SLOW_THRESHOLD_TIMEOUT = 1000

type UseTemplatePaneStateProps = {
    setCurrentPane: (pane: ModalPane) => void
    spaceName?: string
}

export function useTemplatesPaneState({ setCurrentPane, spaceName }: UseTemplatePaneStateProps) {
    const {
        data: templates,
        isFetching,
        isError,
    } = useAppTemplates({
        refetchOnMount: 'always',
    })

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>()
    const selectedTemplate = templates?.find((t) => t.id === selectedTemplateId)

    const isDetail = !!selectedTemplate

    const [isFetchingSlow, setIsFetchingSlow] = useState(false)
    useEffect(() => {
        let timer: number

        if (isFetching) {
            timer = window.setTimeout(() => {
                setIsFetchingSlow(true)
            }, LOADING_SLOW_THRESHOLD_TIMEOUT)
        } else {
            setIsFetchingSlow(false)
        }

        return () => {
            clearTimeout(timer)
        }
    }, [isFetching])

    const onBackToList = () => {
        setSelectedTemplateId(undefined)
    }

    const onStartFromScratch = () => {
        setCurrentPane(ModalPane.CreateApp)
    }

    const onSelectTemplate = (templateId: string) => {
        setSelectedTemplateId(templateId)
    }

    const title = isDetail ? `${selectedTemplate!.name} template` : 'Templates'

    const effectiveIsError = isError && !isFetchingSlow

    const history = useHistory()
    const toast = useToast()

    const { mutateAsync: installAppTemplate } = useInstallAppTemplate()
    const onUseTemplate = async () => {
        if (!selectedTemplateId) {
            return
        }

        try {
            const { stack } = await installAppTemplate({
                templateId: selectedTemplateId,
                stackOptions: {
                    group: spaceName,
                },
            })
            await Promise.all([refetchStacks(), refetchRoles()])
            history.push(getUrl(Urls.Home, stack))
            toast({
                type: 'success',
                startIcon: {
                    name: 'CheckCircle',
                },
                title: 'Template installed successfully',
            })
        } catch (error) {
            console.error(error)
            toast({
                type: 'error',
                startIcon: {
                    name: 'AlertCircle',
                },
                title: 'There was a problem installing the template',
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        }
    }

    return {
        templates,
        isDetail,
        selectedTemplate,
        isFetchingSlow,
        onBackToList,
        onStartFromScratch,
        onSelectTemplate,
        title,
        isError: effectiveIsError,
        onUseTemplate,
    }
}
